import React, { useMemo, useState } from "react"
import { AnimatePresence, motion } from "motion/react"

import { cn } from "@productlane/lib/src/cn"

import type { Attachment } from "../../hooks/useAttachments"
import type { ViewerImage } from "../image-viewer/image-viewer"
import { ImageViewer } from "../image-viewer/image-viewer"
import { AttachmentPreview } from "./attachment-preview"

const isImageAttachment = (a: Attachment) => a.type?.startsWith("image")

interface FileAttachmentsProps {
	attachments: Attachment[]
	onRemove: (id: string) => void
	className?: string
	animateOccurrence?: boolean
	enableViewer?: boolean
	showNameLabel?: boolean
}

export const FileAttachments: React.FC<FileAttachmentsProps> = ({
	attachments,
	onRemove,
	className,
	animateOccurrence = true,
	enableViewer = false,
	showNameLabel = false,
}) => {
	const imageAttachments = useMemo(
		() => attachments.filter(isImageAttachment),
		[attachments],
	)
	const viewerImages = useMemo<ViewerImage[]>(
		() =>
			imageAttachments.map((a) => ({
				src: a.url ?? "",
				alt: a.name,
				name: a.name,
			})),
		[imageAttachments],
	)
	const [viewer, setViewer] = useState<{
		index: number
		originRect: DOMRect
	} | null>(null)

	return (
		<AnimatePresence>
			{attachments.length > 0 && (
				<motion.div
					initial={{ opacity: 0, height: 0, overflow: "hidden" }}
					// Unclipped once open, so the remove button can sit over the corner.
					animate={{
						opacity: 1,
						height: "auto",
						overflow: "hidden",
						transitionEnd: { overflow: "visible" },
					}}
					exit={{ opacity: 0, height: 0, overflow: "hidden" }}
					transition={{
						duration: animateOccurrence ? 0.2 : 0,
						ease: "easeInOut",
					}}
					className="pr-1.5"
				>
					<div className={cn("flex flex-wrap gap-4 p-0.5", className)}>
						{attachments.map((attachment) => (
							<AttachmentPreview
								attachment={attachment}
								onRemove={onRemove}
								key={attachment.id}
								enableViewer={enableViewer}
								showNameLabel={showNameLabel}
								onImageClick={(originRect) => {
									const index = imageAttachments.findIndex(
										(a) => a.id === attachment.id,
									)
									if (index !== -1) setViewer({ index, originRect })
								}}
							/>
						))}
					</div>
					{enableViewer && viewer !== null && (
						<ImageViewer
							images={viewerImages}
							index={viewer.index}
							originRect={viewer.originRect}
							onIndexChange={(index) =>
								setViewer((v) => (v ? { ...v, index } : v))
							}
							onClose={() => setViewer(null)}
						/>
					)}
				</motion.div>
			)}
		</AnimatePresence>
	)
}
